import SmallDelete from '@/components/atom/icon/SmallDelete';
import ImpressionLoggerWithCallback from '@/components/common/ImpressionLoggerWithCallback';
import useAuth from '@/hooks/auth/useAuth';
import useLogger from '@/hooks/useLogger';
import useNavigate from '@/hooks/useNavigate';
import { BannerAdExchangeModel } from '@/models/ad/list';
import theme from '@/styles/theme';
import { isWebView } from '@/utils/mobile/RNPlugins';
import { cn } from '@/utils/style';
import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';

const ANIMATION_DURATION = 300;
const ANIMATION_DELAY = 50;

interface AdExchangeProps {
  fallback: React.ReactNode;
  bannerAdExchange: BannerAdExchangeModel | null;
  onImpressionLog: () => void;
  enableAnimation?: boolean;
}

interface AdExchangeContextValue {
  bannerAdExchange: BannerAdExchangeModel | null;
  handleAdClick: (bannerAdExchange: BannerAdExchangeModel) => void;
}

const AdExchangeContext = createContext<AdExchangeContextValue>({
  bannerAdExchange: null,
  handleAdClick: () => {},
});

const useAdExchangeContext = () => {
  const context = useContext(AdExchangeContext);
  if (!context) {
    throw new Error('useAdExchangeContext must be used within AdExchangeComponent');
  }
  return context;
};

let isAnimationActive = false;

const AdExchangeComponent = (props: PropsWithChildren<AdExchangeProps>) => {
  const logger = useLogger();
  const isAnimationEnabled = () => {
    if (props.enableAnimation && isAnimationActive) {
      return true;
    }
    return !props.enableAnimation;
  };
  const [isVisible, setIsVisible] = useState(() => isAnimationEnabled());

  useEffect(() => {
    if (props.enableAnimation && !isAnimationActive) {
      isAnimationActive = true;
    }
  }, [props.enableAnimation]);

  useEffect(() => {
    if (props.enableAnimation && !isVisible) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, ANIMATION_DELAY);
      return () => clearTimeout(timer);
    }
  }, [props.enableAnimation]);

  const handleAdClick = (bannerAdExchange: BannerAdExchangeModel) => {
    logger.log(
      {
        eventName: 'ad_banner_click',
        payload: {
          targetType: bannerAdExchange.targetPlace,
          bannerAdId: bannerAdExchange.bannerAdId,
          adViewerOsPlatform: bannerAdExchange.adViewerOsPlatform,
          exposedAt: Date.now().valueOf(),
        },
      },
      {
        datadogRum: false,
      }
    );
  };

  const getAnimationClasses = () => {
    if (!props.enableAnimation) return 'w-full';

    return cn(
      'w-full grid overflow-hidden',
      isVisible ? '[grid-template-rows:1fr]' : '[grid-template-rows:0fr]',
      'transition-[grid-template-rows] ease-in-out',
      `duration-[${ANIMATION_DURATION}ms]`
    );
  };

  if (!props.bannerAdExchange) {
    return (
      <div className={getAnimationClasses()}>
        <div className='min-h-0'>{props.fallback}</div>
      </div>
    );
  }

  return (
    <AdExchangeContext.Provider
      value={{
        bannerAdExchange: props.bannerAdExchange,
        handleAdClick,
      }}
    >
      <div className={getAnimationClasses()}>
        <div className='min-h-0'>
          <ImpressionLoggerWithCallback onImpressionLog={props.onImpressionLog}>
            {props.children}
          </ImpressionLoggerWithCallback>
        </div>
      </div>
    </AdExchangeContext.Provider>
  );
};

export default AdExchangeComponent;

const AdContent = ({ children, className }: PropsWithChildren<{ className?: string }>) => {
  return <div className={cn('flex w-full flex-col justify-center gap-[8px]', className)}>{children}</div>;
};

interface BandCtaProps {
  className?: string;
  onClose?: () => void;
}

const BandCta = ({ className, onClose }: BandCtaProps) => {
  const { token } = useAuth();
  const { bannerAdExchange, handleAdClick } = useAdExchangeContext();

  if (!bannerAdExchange) {
    return null;
  }

  const handleActionClick = () => {
    bannerAdExchange.action(token, handleAdClick);
  };

  return (
    <div
      className={cn(
        'flex w-full items-center justify-between bg-gray-700',
        'pt-[12px] pr-[20px] pb-[12px] pl-[12px]',
        className
      )}
    >
      <div className='flex gap-[12px]'>
        {onClose && (
          <button onClick={onClose} aria-label='광고 닫기'>
            <SmallDelete color={theme.color.gray300} size={24} />
          </button>
        )}
        <div className='flex flex-col'>
          <span className='typography-ui-13m text-white'>{bannerAdExchange.subTitle}</span>
          <span className='typography-ui-15sb text-white'>{bannerAdExchange.title}</span>
        </div>
      </div>
      <div>
        <button
          className='typography-ui-13m h-[32px] rounded-lg border bg-primary-600 px-[8px] text-white'
          onClick={handleActionClick}
        >
          {bannerAdExchange.buttonLabel}
        </button>
      </div>
    </div>
  );
};

interface ImageAdProps {
  className?: string;
  isShowAdBadge?: boolean;
}

const ImageAd = ({ className, isShowAdBadge }: ImageAdProps) => {
  const { token } = useAuth();
  const { bannerAdExchange, handleAdClick } = useAdExchangeContext();
  const navigate = useNavigate();

  if (!bannerAdExchange?.bannerImageUrl) {
    return null;
  }

  const handleImageClick = () => {
    if (isWebView()) {
      navigate.navigateToExternalBrowser({ url: bannerAdExchange.getLink(token) });
      return;
    }

    bannerAdExchange.action(token, handleAdClick);
  };

  const altText = bannerAdExchange.title || bannerAdExchange.deliberatedCode || '광고 이미지';

  return (
    <div className='relative'>
      <img
        onClick={handleImageClick}
        className={cn('cursor-pointer object-contain', className)}
        src={bannerAdExchange.bannerImageUrl}
        alt={altText}
      />
      {isShowAdBadge && (
        <div
          className={
            'absolute right-[8px] bottom-[8px] flex h-[20px] w-[32px] items-center justify-center rounded-[6px] bg-dimmed-40'
          }
        >
          <p className={'typography-ui-12m text-white'}>AD</p>
        </div>
      )}
    </div>
  );
};

interface QnaAdProps {
  className?: string;
  isShowAdBadge?: boolean;
}

const QnaAd = ({ className, isShowAdBadge }: QnaAdProps) => {
  const { token } = useAuth();
  const { bannerAdExchange, handleAdClick } = useAdExchangeContext();
  const navigate = useNavigate();

  if (!bannerAdExchange) {
    return null;
  }

  const handleQnaClick = () => {
    if (isWebView()) {
      navigate.navigateToExternalBrowser({ url: bannerAdExchange.getLink(token) });
      return;
    }
    bannerAdExchange.action(token, handleAdClick);
  };

  return (
    <div className={cn('relative hover:bg-gray-30', className)}>
      <div className='box-border flex cursor-pointer flex-col gap-[24px] p-[24px]' onClick={handleQnaClick}>
        <div className='absolute inset-x-0 bottom-0 border-gray-200 border-b' />
        <div className='flex flex-col gap-[12px]'>
          <div className='flex flex-col gap-[12px]'>
            <div className='flex flex-col gap-[12px]'>
              <div className='flex w-full items-center justify-between'>
                <div className='flex-1 pr-[16px]'>
                  <div className='typography-ui-17sb overflow-hidden overflow-ellipsis whitespace-nowrap text-gray-800'>
                    {bannerAdExchange.title}
                  </div>
                  {bannerAdExchange.content && (
                    <div
                      className='typography-ui-13r -webkit-box flex h-[45px] flex-col justify-center overflow-hidden overflow-ellipsis text-gray-500'
                      style={{ WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
                    >
                      {bannerAdExchange.content}
                    </div>
                  )}
                </div>
              </div>
              {bannerAdExchange.badges.length > 0 && (
                <div className='flex items-center gap-[4px]'>
                  {bannerAdExchange.badges.map((badge) => {
                    return (
                      <div
                        key={`badge-${badge.content}`}
                        className='flex h-[24px] items-center justify-center rounded-[4px] bg-gray-20 px-[8px] py-0'
                      >
                        <div className='typography-ui-13sb text-gray-500'>{badge.content}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isShowAdBadge && (
        <div className='absolute top-[12px] right-[12px] flex h-[18px] w-[28px] items-center justify-center rounded-[5px] bg-dimmed-20'>
          <p className='typography-ui-12m text-white'>AD</p>
        </div>
      )}
    </div>
  );
};

AdExchangeComponent.ImageAd = ImageAd;
AdExchangeComponent.Content = AdContent;
AdExchangeComponent.BandCta = BandCta;
AdExchangeComponent.QnaAd = QnaAd;
